'use client'

import { Box, Button, Flex, FormControl, FormErrorMessage, FormLabel, HStack, Heading, Icon, Input, InputGroup, InputRightElement, Link, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, Text, useColorModeValue, useDisclosure, useToast } from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import { useRef, useState } from "react";
import { BsEyeFill, BsFillEyeSlashFill } from 'react-icons/bs';
import NextLink from "next/link";
import { useAction } from "next-safe-action/hook";
import { useRouter } from "next/navigation";
import { createUser, verifyUser } from "./actions";

import ReCAPTCHA from 'react-google-recaptcha';

interface SignUpSchema {
    email: string;
    password: string;
    name: string;
    tel: string;
}

export default function SignUpForm() {

    const { handleSubmit, reset, register, formState: { errors, isSubmitting } } = useForm<SignUpSchema>()
    const toast = useToast()
    const router = useRouter()

    const [showPassword, setShowPassword] = useState(false)

    const [token, setToken] = useState('')
    const recaptchaRef = useRef<any>(null)
    const onCaptchaChange = (token: string | null) => {
        if (token) {
            setToken(token)
        }
    }

    const { execute, status } = useAction(createUser, {
        onSuccess: (data) => {
            // console.log(data);

            if (data?.failure) {
                toast({
                    title: 'Une erreur est survenue!',
                    description: data?.failure.toString(),
                    status: 'error',
                    isClosable: true,
                    position: 'top'
                })
            } else {
                toast({
                    title: 'Opération réussie!',
                    description: 'Votre compte été créé avec succès. Nous vous avons envoyé un mail de vérification. Veuillez vérifier votre email',
                    status: 'success',
                    isClosable: true,
                    position: 'top',
                    duration: null
                })

                reset()
            }
        },
        onError: (error) => {
            // console.log("Une erreur: ", error);

            toast({
                title: 'Error',
                description: 'Votre compte n\'a pas été créé. Veuillez vérifier vos paramètres',
                status: 'error',
                isClosable: true,
                position: 'top'
            })
        }
    });

    async function handleForm(values: SignUpSchema) {
        if (!token) return;
        execute({ ...values, captchaToken: token })
        recaptchaRef?.current?.reset() 
    }

    return (
        <Flex
            minH="100vh"
            align="center"
            justify="center"
            bg={useColorModeValue('gray.100', 'gray.800')}
        >
            <form onSubmit={handleSubmit(handleForm)}>
                <Stack spacing={8} mx="auto" maxW="xl" py={12} px={6}>
                    <Stack align="center">
                        <Heading fontSize="4xl">{"S'inscrire"}</Heading>
                        <Text fontSize="lg" color="gray.600">
                            pour profiter de nos <Link href="#" color="blue.500">fonctionnalités</Link> ✌️
                        </Text>
                    </Stack>
                    <Box
                        rounded="lg"
                        bg={useColorModeValue('white', 'gray.700')}
                        boxShadow="lg"
                        p={8}
                    >
                        <Stack spacing={4}>
                            <FormControl id="name" isInvalid={!!errors.name}>
                                <FormLabel>Nom Complet</FormLabel>
                                <Input type="text"
                                    {
                                    ...register('name', {
                                        required: 'Veuillez renseigner votre nom complet',
                                        setValueAs: v => v.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;')
                                    })
                                    }
                                />
                                <FormErrorMessage>
                                    {errors['name'] && errors['name'].message}
                                </FormErrorMessage>
                            </FormControl>

                            <HStack>
                                <Box>
                                    <FormControl id="email" isInvalid={!!errors.email}>
                                        <FormLabel>Email</FormLabel>
                                        <Input type="email"
                                            {
                                            ...register('email', {
                                                required: "Veuillez renseigner votre email",
                                                setValueAs: v => v.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;')
                                            })
                                            }
                                        />
                                        <FormErrorMessage>
                                            {errors["email"] && errors["email"].message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>
                                <Box>
                                    <FormControl id="tel" isInvalid={!!errors.tel}>
                                        <FormLabel>Tel</FormLabel>
                                        <Input type="text"
                                            {
                                            ...register('tel', {
                                                required: "Veuillez renseigner votre numéro de téléphone",
                                                setValueAs: v => v.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;')
                                            })
                                            }
                                        />
                                        <FormErrorMessage>
                                            {errors["tel"] && errors["tel"].message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>
                            </HStack>

                            <FormControl id="password" isInvalid={!!errors.password}>
                                <FormLabel>Password</FormLabel>
                                <InputGroup size='md'>
                                    <Input
                                        pr='4.5rem'
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder='Enter password'
                                        {
                                        ...register('password', {
                                            required: 'Veuillez renseigner votre mot de passe',
                                            minLength: {
                                                value: 8,
                                                message: "Le mot de passe doit contenir au moins 8 charactères"
                                            },
                                            setValueAs: v => v.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;')
                                        })
                                        }
                                    />
                                    <InputRightElement width='4.5rem'>
                                        <Button h='1.75rem' size='sm' onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <Icon as={BsEyeFill} /> : <Icon as={BsFillEyeSlashFill} />}
                                        </Button>
                                    </InputRightElement>
                                </InputGroup>
                                <FormErrorMessage>
                                    {errors['password'] && errors['password'].message}
                                </FormErrorMessage>
                            </FormControl>
                        </Stack>

                        <Box mt={5}>
                            <ReCAPTCHA
                                size="normal"
                                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY ?? "NOT DEFINED"}
                                onChange={onCaptchaChange}
                                ref={recaptchaRef}
                            />
                        </Box>



                        <Stack spacing={10}>
                            <Button
                                type="submit"
                                mt="7"
                                colorScheme="brand"
                                isLoading={status && status === "executing"}
                                isDisabled={!token}
                            >
                                {"S'inscrire"}
                            </Button>
                        </Stack>
                        <Stack pt={6}>
                            <Text align="center">
                                Vous avez déjà un compte? <Link as={NextLink} href="/auth/signin" color="blue.500" >{"Se connecter"}</Link>
                            </Text>
                            <Text align="center">
                                Vous {"n'avez"} pas reçu de lien de vérification? <VerifyEmailModal />
                            </Text>
                        </Stack>
                    </Box>
                </Stack>
            </form>
        </Flex>
    )
}

function VerifyEmailModal() {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [email, setEmail] = useState('')

    const [token, setToken] = useState('')
    const recaptchaRef = useRef<any>(null)
    const onCaptchaChange = (token: string | null) => {
        if (token) {
            setToken(token)
        }
    }

    const toast = useToast()

    const { execute, status } = useAction(verifyUser, {
        onSuccess: (data) => {
            // console.log(data);

            if (data?.failure) {
                toast({
                    title: 'Une erreur est survenue!',
                    description: data?.failure.toString(),
                    status: 'error',
                    isClosable: true,
                    position: 'top'
                })
            } else {
                toast({
                    title: 'Opération réussie!',
                    description: data?.success,
                    status: 'success',
                    variant: 'top-accent',
                    isClosable: true,
                    position: 'top',
                    duration: null
                })
            }
        },
        onError: (error) => {
            // console.log("Une erreur: ", error);

            toast({
                title: 'Error',
                description: 'Veuillez vérifier vos paramètres',
                status: 'error',
                isClosable: true,
                position: 'top'
            })
        }
    });

    async function handleSubmit() {
        if (!token) return;
        execute({ email, captchaToken:token })
        setEmail('')
        recaptchaRef?.current?.reset() 
        onClose()
    }

    return (
        <>
            <Link onClick={onOpen} color="blue.500" >{"Vérifier"}</Link>

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Vérifier mon compte créé</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl id="email" isRequired>
                            <FormLabel>Email</FormLabel>
                            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
                        </FormControl>
                        <Box mt={5}>
                            <ReCAPTCHA
                                size="normal"
                                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY ?? "NOT DEFINED"}
                                onChange={onCaptchaChange}
                                ref={recaptchaRef}
                            />
                        </Box>
                    </ModalBody>

                    <ModalFooter>
                        <Button mr={3} colorScheme='blue' isDisabled={!token} onClick={handleSubmit} isLoading={status && status === "executing"}>Envoyer</Button>
                        <Button onClick={onClose}>
                            Annuler
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    )
}
